import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/apiUtils';
import { Container, Card, Row, Col, Badge, Button, Modal, Form, Alert, Table } from 'react-bootstrap';
import { useNotification, NotificationMessages } from '../hooks/useNotification';
import { getItemPermissions, canToggleEditLock } from '../utils/permissionUtils';
import ProjectEditModal from './ProjectEditModal';

const ProjectView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);

  // Helper function to format time from 24-hour to 12-hour AM/PM format
  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return '';
    
    // Handle different time formats (HH:MM:SS or HH:MM)
    const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!timeMatch) return timeString;
    
    const hours = parseInt(timeMatch[1]);
    const minutes = timeMatch[2];
    
    if (hours === 0) {
      return `12:${minutes} AM`;
    } else if (hours < 12) {
      return `${hours}:${minutes} AM`;
    } else if (hours === 12) {
      return `12:${minutes} PM`;
    } else {
      return `${hours - 12}:${minutes} PM`;
    }
  };

  // Helper function to format number with commas and decimal places (same as ProjectProposal)
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    
    // Convert to string if it's a number
    const stringValue = String(value);
    
    // Remove all characters except digits and decimal point
    const cleanValue = stringValue.replace(/[^\d.]/g, '');
    
    // Split by decimal point
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Add commas to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // If there's a decimal part, limit to 2 decimal places
    if (decimalPart !== undefined) {
      const limitedDecimal = decimalPart.substring(0, 2);
      return formattedInteger + '.' + limitedDecimal;
    }
    
    return formattedInteger;
  };
  const [loading, setLoading] = useState(true);
  const [showDecision, setShowDecision] = useState(false);
  const [approvalTarget, setApprovalTarget] = useState(null); // 'project' or 'budget'
  const [decision, setDecision] = useState('Approved');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionReason, setCompletionReason] = useState('');
  const [isEarlyCompletion, setIsEarlyCompletion] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDocxTemplateModal, setShowDocxTemplateModal] = useState(false);
  const { notification, showSuccess, showError, dismiss } = useNotification();
  const [budget, setBudget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editLockLoading, setEditLockLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [generatingDocx, setGeneratingDocx] = useState(false);

  // Toggle edit lock function
  const handleToggleEditLock = async () => {
    if (!project) return;
    setEditLockLoading(true);
    try {
      const newLockState = !project.edit_locked;
      await apiClient.put(`/projects/${project.project_id}/edit-lock`, { edit_locked: newLockState });
      setProject(prev => ({ ...prev, edit_locked: newLockState }));
      setSuccessMessage(`Edit permissions ${newLockState ? 'locked' : 'unlocked'} successfully.`);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Failed to toggle edit lock:', err);
      showError('Failed to toggle edit lock');
    } finally {
      setEditLockLoading(false);
    }
  };

  // Helper function to parse names and tasks
  const parseNameTaskList = (listString) => {
    if (!listString) return [];
    
    // Handle JSON arrays like ["Head","GAD"]
    if (typeof listString === 'string' && listString.startsWith('[') && listString.endsWith(']')) {
      try {
        const parsed = JSON.parse(listString);
        if (Array.isArray(parsed)) {
          return parsed.map(name => ({ name: name.trim(), task: '' }));
        }
      } catch (e) {
        // Fall through to string parsing
      }
    }
    
    // Handle string format like "Name - Task, Name - Task"
    if (typeof listString === 'string') {
      const results = [];
      const regex = /([^,]+?)\s-\s(.*?)(?:,\s(?=[^,]+?\s-\s)|$)/g;
      let match;
      while ((match = regex.exec(listString)) !== null) {
        results.push({ name: match[1].trim(), task: match[2].trim() });
      }
      if (results.length === 0 && listString.trim()) {
        // Handle comma-separated names without tasks (e.g., "Head, Vice Chancellor, Chancellor")
        if (listString.includes(',')) {
          const names = listString.split(',').map(name => name.trim()).filter(name => name);
          return names.map(name => ({ name, task: '' }));
        } else {
          // Fallback: treat entire string as a name
          results.push({ name: listString.trim(), task: '' });
        }
      }
      return results;
    }
    
    return [];
  };

  // Helper function to merge names with tasks
  const mergeNamesWithTasks = (names, tasks, ids, departments) => {
    const parsedNames = parseNameTaskList(names);
    const parsedTasks = tasks ? 
      (typeof tasks === 'string' ? JSON.parse(tasks) : tasks) : 
      {};
    const parsedIds = ids ? 
      (typeof ids === 'string' ? JSON.parse(ids) : ids) : 
      [];
    const parsedDepartments = departments ? 
      (typeof departments === 'string' ? JSON.parse(departments) : departments) : 
      {};
    
    return parsedNames.map((item, index) => {
      // Extract name only (remove task if it's in the name string)
      let nameOnly = item.name;
      if (typeof nameOnly === 'string' && nameOnly.includes(' - ')) {
        nameOnly = nameOnly.split(' - ')[0].trim();
      }
      
      let task = '';
      let department = '';
      
      // Try to find task by user ID if available
      if (parsedIds[index] && parsedTasks[parsedIds[index]]) {
        task = parsedTasks[parsedIds[index]];
      } else if (item.task) {
        // Fallback to item.task if available
        task = item.task;
      }
      
      // Try to find department by user ID if available
      if (parsedIds[index] && parsedDepartments[parsedIds[index]]) {
        department = parsedDepartments[parsedIds[index]];
      }
      
      // Also check for department_college field (like template does)
      if (!department && item.department_college) {
        department = item.department_college;
      }
      
      return {
        name: nameOnly,
        task: task,
        department: department
      };
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await apiClient.get(`/projects/${id}`, { timeout: 30000 });
        setProject(data);
        // Try loading linked budget
        try {
          const { data: budgetData } = await apiClient.get(`/budgets/by-project/${id}`, { timeout: 30000 });
          setBudget(budgetData);
        } catch {}
      } catch (e) {
        console.error('Error loading project:', e);
        // If API denies access, send back to projects list
        navigate('/projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);


  const elevated = user && ['Extension Head','GAD','Vice Chancellor for Administration and Finance','Vice Chancellor for Research, Development and Extension Services','Chancellor','Admin'].includes(user.role);

  const hasGenderEquality = (p) => {
    const sdgs = asList(p.sdg_goals);
    return sdgs.includes('Gender Equality');
  };

  const canCurrentUserDecide = (p) => {
    if (!user) return false;
    const role = user.role;
    const s = p.status;
    const needsGAD = hasGenderEquality(p);
    
    // Robust STF/MDS detection across formats
    const fundSource = asList(p.fund_source);
    let hasSTFMDS = false;
    if (Array.isArray(fundSource)) {
      hasSTFMDS = fundSource.includes('STF') || fundSource.includes('MDS');
    }
    if (!hasSTFMDS && typeof p.fund_source === 'string') {
      try {
        if (p.fund_source.trim().startsWith('[')) {
          const parsed = JSON.parse(p.fund_source);
          if (Array.isArray(parsed)) {
            hasSTFMDS = parsed.includes('STF') || parsed.includes('MDS');
          }
        } else {
          hasSTFMDS = p.fund_source.includes('STF') || p.fund_source.includes('MDS');
        }
      } catch (_e) {
        // fall through to final string check
      }
    }
    if (!hasSTFMDS && p.fund_source) {
      const fundSourceStr = String(p.fund_source);
      hasSTFMDS = fundSourceStr.includes('STF') || fundSourceStr.includes('MDS');
    }
    
    console.log('🔍 DECISION DEBUG:', {
      role,
      status: s,
      fundSource: p.fund_source,
      parsedFundSource: fundSource,
      hasSTFMDS,
      needsGAD
    });
    
    if (role === 'GAD') {
      return s === 'Pending' && needsGAD;
    }
    if (role === 'Extension Head') {
      return (s === 'Pending' && !needsGAD) || s === 'Reviewed by GAD';
    }
    if (role === 'Vice Chancellor for Research, Development and Extension Services') {
      return s === 'Approved by Extension Head';
    }
    if (role === 'Vice Chancellor for Administration and Finance') {
      return s === 'Approved by VC-RDES' && hasSTFMDS;
    }
    if (role === 'Chancellor') {
      // Chancellor is the final approver for all projects
      // Can decide on projects that are ready for Chancellor review
      const canDecide = s === 'Approved by VC-AF' || s === 'Approved by VC-RDES';
      console.log('🔍 CHANCELLOR DECISION:', {
        hasSTFMDS,
        status: s,
        canDecide,
        isVCAF: s === 'Approved by VC-AF',
        isVCRDES: s === 'Approved by VC-RDES'
      });
      return canDecide;
    }
    return false;
  };

  const hasUserAlreadyApprovedProject = (p) => {
    if (!user) return false;
    const role = user.role;
    const s = p.status;
    
    // Check if current user's role has already approved
    if (role === 'GAD') {
      return ['Reviewed by GAD', 'Approved by Extension Head', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor', 'On-Going', 'Completed'].includes(s);
    }
    if (role === 'Extension Head') {
      return ['Approved by Extension Head', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor', 'On-Going', 'Completed'].includes(s);
    }
    if (role === 'Vice Chancellor for Research, Development and Extension Services') {
      return ['Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor', 'On-Going', 'Completed'].includes(s);
    }
    if (role === 'Vice Chancellor for Administration and Finance') {
      return ['Approved by VC-AF', 'Approved by Chancellor', 'On-Going', 'Completed'].includes(s);
    }
    if (role === 'Chancellor') {
      return ['Approved by Chancellor', 'On-Going', 'Completed'].includes(s);
    }
    // Admin can always approve (not restricted)
    return false;
  };

  // Helper functions for project actions
  const canStartProject = (project) => {
    if (!project.start_date) return true; // If no start date, can start anytime
    const today = new Date();
    const startDate = new Date(project.start_date);
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    return today >= startDate;
  };

  const canCompleteProject = (project) => {
    // Anyone can complete anytime - no date restrictions
    return true;
  };


  const submitDecision = async () => {
    try {
      setError('');
      
      // Validate decision is set
      if (!decision || (decision !== 'Approved' && decision !== 'Rejected')) {
        console.error('❌ Invalid decision value:', decision);
        showError('Invalid decision. Please try again.');
        return;
      }
      
      console.log('🔍 Submitting decision:', { decision, approvalTarget, hasBudget: !!budget, budgetId: budget?.budget_id });
      
      // Default to 'project' if approvalTarget is not set (shouldn't happen, but safety check)
      const target = approvalTarget || 'project';
      console.log('🔍 Target determined:', target);
      
      if (target === 'budget' && budget && budget.budget_id) {
        console.log('🔍 Approving/rejecting LIB budget');
        // Approve/reject LIB budget
        // Send undefined for empty remarks so validator skips it entirely
        const requestBody = { 
          decision
        };
        if (remarks && remarks.trim()) {
          requestBody.remarks = remarks.trim();
        }
        
        await apiClient.post(`/budgets/${budget.budget_id}/decision`, requestBody, { timeout: 30000 });
        
        // Reload budget data
        try {
          const budgetResponse = await apiClient.get(`/budgets/by-project/${id}`, { timeout: 30000 });
          setBudget(budgetResponse.data);
        } catch (e) {
          console.error('Failed to reload budget:', e);
        }
        
        if (decision === 'Approved') {
          showSuccess('Line-Item Budget approved successfully!');
        } else {
          showSuccess('Line-Item Budget rejected successfully!');
        }
      } else {
        // Approve/reject Project Proposal
        console.log('🔍 Approving/rejecting Project Proposal');
        
        // Ensure decision is explicitly set (fallback to 'Approved' if somehow undefined)
        const finalDecision = decision || 'Approved';
        console.log('🔍 Request payload:', { 
          decision: finalDecision, 
          originalDecision: decision,
          remarks: remarks || null, 
          projectId: id 
        });
        
        await apiClient.post(`/projects/${id}/decision`, { 
          decision: finalDecision, 
          remarks: remarks && remarks.trim() ? remarks.trim() : undefined
        }, { timeout: 30000 });
        
        const { data } = await apiClient.get(`/projects/${id}`, { timeout: 30000 });
        setProject(data);
        
        // Reload budget if it exists (to refresh status)
        try {
          const budgetResponse = await apiClient.get(`/budgets/by-project/${id}`, { timeout: 30000 });
          setBudget(budgetResponse.data);
        } catch (e) {
          // Budget might not exist, ignore
        }
        
        // Show success message
        if (decision === 'Approved') {
          showSuccess(NotificationMessages.PROJECT_APPROVED);
        } else if (decision === 'Rejected') {
          showSuccess(NotificationMessages.PROJECT_REJECTED);
        }
      }
      
      setShowDecision(false);
      setRemarks('');
      setApprovalTarget(null);
      console.log('✅ Decision submitted successfully');
    } catch (e) {
      console.error('❌ Error submitting decision:', e);
      console.error('❌ Error response:', e.response);
      console.error('❌ Error response data:', e.response?.data);
      console.error('❌ Error message:', e.message);
      
      // Handle validation errors specifically
      if (e.response?.status === 400) {
        const errorData = e.response.data;
        console.error('❌ 400 Error details:', errorData);
        
        // Try to get a user-friendly error message
        let errorMessage = 'Invalid request. Please try again.';
        
        if (errorData.details) {
          // Use the details field if available (human-readable)
          errorMessage = errorData.details;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Express-validator format
          errorMessage = errorData.errors.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
        
        showError(errorMessage);
        setError(errorMessage);
      } else {
        const errorMessage = e.response?.data?.error || e.response?.data?.message || e.message || 'Failed to submit decision. Please try again.';
        showError(errorMessage);
        setError(errorMessage);
      }
    }
  };

  // Handle starting a project
  const handleStartProject = async () => {
    try {
      setError('');
      const { data } = await apiClient.put(`/projects/${id}/start`, {}, { timeout: 30000 });
      setProject(data);
      showSuccess(NotificationMessages.PROJECT_STARTED);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to start project. Please try again.';
      showError(errorMsg);
      setError(errorMsg);
    }
  };

  // Handle opening the complete project modal
  const handleCompleteProject = () => {
    // Check if completing early (before end date)
    const today = new Date();
    const endDate = new Date(project.end_date);
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    const isEarly = project.end_date && today < endDate;
    setIsEarlyCompletion(isEarly);
    setCompletionReason('');
    setError('');
    setShowCompleteModal(true);
  };

  // Handle submitting the completion
  const submitCompletion = async () => {
    try {
      setError('');
      
      // Validate early completion reason if needed
      if (isEarlyCompletion && !completionReason.trim()) {
        setError('A reason is required for early completion.');
        return;
      }
      
      const requestBody = {};
      if (isEarlyCompletion && completionReason.trim()) {
        requestBody.early_completion_reason = completionReason.trim();
      }
      
      const { data } = await apiClient.put(`/projects/${id}/complete`, requestBody, { timeout: 30000 });
      setProject(data);
      setShowCompleteModal(false);
      
      // Show success message
      setError('');
      if (isEarlyCompletion) {
        showSuccess(NotificationMessages.PROJECT_COMPLETED_EARLY);
      } else {
        showSuccess(NotificationMessages.PROJECT_COMPLETED);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to complete project. Please try again.';
      showError(errorMsg);
      setError(errorMsg);
    }
  };

  // Handle reproposing a rejected project
  const handleRepropose = async () => {
    try {
      setError('');
      const { data } = await apiClient.put(`/projects/${id}/repropose`, {}, { timeout: 30000 });
      setProject(data);
      showSuccess(NotificationMessages.PROJECT_REPROPOSED);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to repropose project. Please try again.';
      showError(errorMsg);
      setError(errorMsg);
    }
  };

  // Handle print functionality for Extension Line-Item Budget
  const handlePrintBudget = async () => {
    try {
      setError('');
      // Generate the budget template from project data
      const response = await apiClient.post('/projects/generate-budget-template', {
        project_id: project.project_id
      }, {
        responseType: 'text',
        timeout: 60000 // 60 seconds timeout for template generation
      });
      
      // Create the print HTML with the budget template content
      const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Extension Line-Item Budget</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; }
            @page { size: A4; margin: 12mm; }
            #template-container { 
              width: 100%; 
              border: 2px solid #000; 
              background: #fff; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
              page-break-inside: avoid;
            }
            table { width: 100%; border-collapse: collapse; }
            td, th { border: 1px solid #000; padding: 6px 8px; vertical-align: middle; }
            .checkbox { display: inline-block; width: 14px; height: 14px; border: 1px solid #000; margin-right: 8px; vertical-align: middle; box-sizing: border-box; }
            .checkbox--filled { background: #000; }
          </style>
        </head>
        <body>
          <div id="template-container" style="maxWidth: 900px; margin: 0 auto;">
            ${response.data}
          </div>
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); window.close(); }, 50);
            };
          </script>
        </body>
      </html>`;
      
      const printWindow = window.open('', '_blank', 'width=900,height=1200');
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.error('Error generating budget template:', error);
      let errorMsg = 'Failed to generate budget template for printing. Please try again.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Request timed out. The server may be taking longer than expected. Please try again.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showError(errorMsg);
      setError(errorMsg);
    }
  };

  // Handle template selection
  const handleTemplateSelection = () => {
    setShowTemplateModal(true);
  };

  // Handle delete flow
  const handleDeleteProject = async () => {
    try {
      const response = await apiClient.delete(`/projects/${project.project_id}`, { timeout: 30000 });
      if (response && response.status >= 200 && response.status < 300) {
        showSuccess(NotificationMessages.PROJECT_DELETED);
        navigate('/projects', { state: { success: NotificationMessages.PROJECT_DELETED } });
      }
    } catch (e) {
      showError(e.response?.data?.error || 'Failed to delete project. Please try again.');
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Handle DOCX generation for Project Proposal using DocXTemplater
  const handleGenerateProjectProposalDocx = async () => {
    if (!project || generatingDocx) return;

    try {
      setGeneratingDocx(true);
      setError('');
      setShowDocxTemplateModal(false);

      const response = await apiClient.post(
        `/projects/${project.project_id}/generate-docx`,
        {},
        {
          responseType: 'blob',
          validateStatus: (status) => status < 500
        }
      );

      // Check if response is actually an error (JSON) or a blob (DOCX)
      if (response.headers['content-type']?.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.message || errorData.error || 'Failed to generate document');
        return;
      }

      // Success - it's a DOCX file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Project_Proposal_${(project.title || 'Project').replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Project Proposal document generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating DOCX document:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to generate document. Please try again.';
      showError(errorMessage);
      setError(errorMessage);
    } finally {
      setGeneratingDocx(false);
    }
  };

  // Handle DOCX generation for LIB using DocXTemplater
  const handleGenerateLibDocx = async () => {
    if (!project || generatingDocx) return;

    try {
      setGeneratingDocx(true);
      setError('');
      setShowDocxTemplateModal(false);

      const response = await apiClient.post(
        '/projects/generate-budget-template',
        { project_id: project.project_id },
        {
          responseType: 'blob',
          validateStatus: (status) => status < 500
        }
      );

      // Check if response is actually an error (JSON) or a blob (DOCX)
      if (response.headers['content-type']?.includes('application/json')) {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        showError(errorData.message || errorData.error || 'Failed to generate document');
        return;
      }

      // Success - it's a DOCX file
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `extension-line-item-budget-${project.project_id}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Line-Item Budget document generated successfully!');
    } catch (error) {
      console.error('Error generating LIB DOCX:', error);
      let errorMsg = 'Failed to generate Line-Item Budget document. Please try again.';
      
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showError(errorMsg);
      setError(errorMsg);
    } finally {
      setGeneratingDocx(false);
    }
  };

  // Handle print functionality for Project Proposal (fetches form content and prints it)
  const handlePrintProjectProposal = async () => {
    try {
      setError('');
      // Generate the form from project data with explicit timeout
      const response = await apiClient.post('/projects/generate-form', {
        project_id: project.project_id
      }, {
        responseType: 'text',
        timeout: 60000 // 60 seconds timeout for template generation
      });
      
      // Create the print HTML with the form content
      const html = `<!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Extension Program Plan/Proposal</title>
          <style>
            * { box-sizing: border-box; }
            html, body { margin: 0; padding: 0; background: #fff; }
            @page { size: A4; margin: 12mm; }
            #template-container { 
              width: 100%; 
              border: 2px solid #000; 
              background: #fff; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
              page-break-inside: avoid;
            }
            table { width: 100%; border-collapse: collapse; }
            td, th { border: 1px solid #000; padding: 6px 8px; vertical-align: middle; }
            .checkbox { display: inline-block; width: 14px; height: 14px; border: 1px solid #000; margin-right: 8px; vertical-align: middle; flex-shrink: 0; box-sizing: border-box; }
            .checkbox--filled { background: #000; }
          </style>
        </head>
        <body>
          ${response.data}
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); window.close(); }, 50);
            };
          <\/script>
        </body>
      </html>`;
      
      const printWindow = window.open('', '_blank', 'width=900,height=1200');
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.error('Error generating project form:', error);
      let errorMsg = 'Failed to generate form for printing. Please try again.';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = 'Request timed out. The server may be taking longer than expected. Please try again.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showError(errorMsg);
      setError(errorMsg);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (!project) return <div className="p-4">Not found</div>;

  const asList = (value) => {
    if (!value) return [];
    try { const arr = JSON.parse(value); return Array.isArray(arr) ? arr : []; } catch { return []; }
  };

  const renderMonitoringEvaluationTable = (monitoringData) => {
    // Try to render rows if provided as monitoring_evaluation_table.rows
    let rows = [];
    try {
      const parsed = typeof monitoringData === 'string' ? JSON.parse(monitoringData) : monitoringData;
      if (parsed && Array.isArray(parsed.rows)) rows = parsed.rows;
    } catch {}
    return (
      <div className="table-responsive">
        <style>
          {`
            .me-view-table {
              table-layout: fixed !important;
              width: 100% !important;
              border-collapse: collapse !important;
            }
            .me-view-table th:last-child,
            .me-view-table td:last-child {
              border-right: 1px solid #dee2e6 !important;
            }
            .me-view-table th:last-child::after,
            .me-view-table td:last-child::after {
              display: none !important;
              content: none !important;
            }
            .me-view-table th:last-child::before,
            .me-view-table td:last-child::before {
              display: none !important;
              content: none !important;
            }
            .me-view-table *::after {
              display: none !important;
              content: none !important;
            }
            .me-view-table *::before {
              display: none !important;
              content: none !important;
            }
            .me-view-table th,
            .me-view-table td {
              position: relative !important;
            }
            .me-view-table th:last-child,
            .me-view-table td:last-child {
              position: relative !important;
            }
            .me-view-table th:last-child::after,
            .me-view-table td:last-child::after {
              position: absolute !important;
              top: 0 !important;
              right: 0 !important;
              width: 0 !important;
              height: 0 !important;
              display: none !important;
              content: none !important;
            }
          `}
        </style>
        <table className="table table-bordered table-sm me-view-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead className="table-primary">
            <tr>
              <th style={{ width: '8%', fontSize: '0.9rem', padding: '12px 8px' }}>Objectives</th>
              <th style={{ width: '12%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Indicators</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Baseline Data</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Performance Target</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Data Source</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Collection Method</th>
              <th style={{ width: '10%', fontSize: '0.9rem', padding: '12px 8px' }}>Frequency of Data Collection</th>
              <th style={{ width: '18%', fontSize: '0.9rem', padding: '12px 8px' }}>Office/Persons Responsible</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={`mev-${idx}`} style={{ verticalAlign: 'top' }}>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.objectives || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_indicators || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.baseline_data || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.performance_target || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.data_source || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.collection_method || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.frequency || '—'}</td>
                <td style={{ padding: '8px', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{r.responsible || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderBudgetTable = (budgetRow) => {
    if (!budgetRow) return null;
    let items = {};
    let totals = {};
    try { items = JSON.parse(budgetRow.items_json || '{}') || {}; } catch {}
    try { totals = JSON.parse(budgetRow.totals_json || '{}') || {}; } catch {}

    const mooeItems = [
      ['travel_local', 'Travelling Cost - Local'],
      ['travel_foreign', 'Travelling Cost - Foreign'],
      ['training', 'Training Expenses'],
      ['office_supplies', 'Supplies and Materials - Office Supplies'],
      ['accountable_forms', 'Supplies and Materials - Accountable Forms'],
      ['drugs_medicines', 'Supplies and Materials - Drugs and Medicines'],
      ['laboratory_exp', 'Supplies and Materials - Laboratory Expenses'],
      ['textbooks', 'Supplies and Materials - Textbook and Instructional Materials'],
      ['supplies_others', 'Supplies and Materials - Others'],
      ['postage', 'Postage and Deliveries'],
      ['tel_mobile', 'Communication Expenses - Telephone (Mobile)'],
      ['internet', 'Communication Expenses - Internet'],
      ['comm_others', 'Communication Expenses - Others'],
      ['rent', 'Rent Expenses'],
      ['transport_delivery', 'Transportation and Delivery Expenses'],
      ['subscription', 'Subscription Expenses'],
      ['consultancy', 'Professional Services - Consultancy Services'],
      ['general_services', 'Professional Services - General Services'],
      ['other_professional', 'Professional Services - Other Professional Services'],
      ['repair_it', 'Repairs and Maintenance of Facilities - IT Equipment and Software'],
      ['repair_lab', 'Repairs and Maintenance of Facilities - Laboratory Equipment'],
      ['repair_tech', 'Repairs and Maintenance - Technical and Scientific Equipment'],
      ['repair_machine', 'Repairs and Maintenance - Machineries and Equipment'],
      ['repair_others', 'Repairs and Maintenance - Others'],
      ['taxes', 'Taxes, Duties, Patent, and Licenses'],
      ['other_mooe', 'Other Maintenance and Operating Expenses'],
      ['representation', 'Representation Expenses'],
      ['advertising', 'Advertising Expenses'],
      ['printing_binding', 'Printing and Binding Expenses'],
      ['others_financial_charges', 'Others Financial Charges (Contingency = 10% of the Total Project Cost)'],
    ];
    const equipmentItems = [
      ['equip_it', 'IT Equipment and Software'],
      ['equip_machineries', 'Machineries'],
      ['equip_comm', 'Communication Equipment'],
      ['equip_lab', 'Laboratory Equipment'],
      ['equip_tech_sci', 'Technical and Scientific Equipment'],
      ['equip_others', 'Others'],
    ];

    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const sum = (pairs) => pairs.reduce((acc, [key]) => acc + num(items[key]), 0);
    const mooeSubtotal = totals.mooeSubtotal ?? sum(mooeItems);
    const equipmentSubtotal = totals.equipmentSubtotal ?? sum(equipmentItems);
    const grandTotal = totals.grandTotal ?? mooeSubtotal + equipmentSubtotal;
    const currency = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // LIB approval workflow functions
    const getLibStatusVariant = (status) => {
      if (!status || status === 'Pending') return 'warning';
      if (status.includes('Approved')) return 'success';
      if (status.includes('Rejected')) return 'danger';
      return 'secondary';
    };

    const canCurrentUserDecideLib = () => {
      if (!user || !budgetRow) return false;
      const role = user.role;
      const status = budgetRow.status || 'Pending';
      
      // GAD is NOT part of LIB approval workflow - explicitly exclude
      if (role === 'GAD') return false;
      
      if (role === 'Extension Head') return status === 'Pending';
      if (role === 'Accounting Office') return status === 'Approved by Extension Head';
      if (role === 'Vice Chancellor for Research, Development and Extension Services') return status === 'Approved by Accounting Office';
      if (role === 'Vice Chancellor for Administration and Finance') return status === 'Approved by VC-RDES';
      if (role === 'Chancellor') return status === 'Approved by VC-AF';
      if (role === 'Admin') {
        // Admin can approve at any stage
        return !status.includes('Rejected') && status !== 'Approved by Chancellor';
      }
      return false;
    };

    const hasUserAlreadyApprovedLib = () => {
      if (!user || !budgetRow) return false;
      const role = user.role;
      const status = budgetRow.status || 'Pending';
      
      // Check if current user's role has already approved
      if (role === 'Extension Head') {
        return ['Approved by Extension Head', 'Approved by Accounting Office', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status);
      }
      if (role === 'Accounting Office') {
        return ['Approved by Accounting Office', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status);
      }
      if (role === 'Vice Chancellor for Research, Development and Extension Services') {
        return ['Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status);
      }
      if (role === 'Vice Chancellor for Administration and Finance') {
        return ['Approved by VC-AF', 'Approved by Chancellor'].includes(status);
      }
      if (role === 'Chancellor') {
        return status === 'Approved by Chancellor';
      }
      // Admin can always approve (not restricted)
      return false;
    };

    const getNextLibApprovalStep = () => {
      const status = budgetRow.status || 'Pending';
      if (status.includes('Rejected')) return null;
      if (status === 'Pending') return 'To be reviewed by Extension Head';
      if (status === 'Approved by Extension Head') return 'To be reviewed by Accounting Office';
      if (status === 'Approved by Accounting Office') return 'To be reviewed by VC-RDES';
      if (status === 'Approved by VC-RDES') return 'To be reviewed by VC-AF';
      if (status === 'Approved by VC-AF') return 'To be reviewed by Chancellor';
      if (status === 'Approved by Chancellor') return null;
      return null;
    };

    const getLibApprovalChecklist = () => {
      const status = budgetRow.status || 'Pending';
      const steps = [
        {
          key: 'eh',
          label: 'Approved by Extension Head',
          done: ['Approved by Extension Head', 'Approved by Accounting Office', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status),
          rejected: status === 'Rejected by Extension Head'
        },
        {
          key: 'accounting',
          label: 'Approved by Accounting Office',
          done: ['Approved by Accounting Office', 'Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status),
          rejected: status === 'Rejected by Accounting Office'
        },
        {
          key: 'vcrdes',
          label: 'Approved by VC-RDES',
          done: ['Approved by VC-RDES', 'Approved by VC-AF', 'Approved by Chancellor'].includes(status),
          rejected: status === 'Rejected by VC-RDES'
        },
        {
          key: 'vcaf',
          label: 'Approved by VC-AF',
          done: ['Approved by VC-AF', 'Approved by Chancellor'].includes(status),
          rejected: status === 'Rejected by VC-AF'
        },
        {
          key: 'ch',
          label: 'Approved by Chancellor',
          done: status === 'Approved by Chancellor',
          rejected: status === 'Rejected by Chancellor'
        }
      ];
      return steps;
    };

    return (
      <div className="mt-3">
        <Row className="mb-3">
          <Col md={6}>
            <h5 className="mb-1">Extension Line-Item Budget</h5>
            <div className="text-muted small">Submitted on {new Date(budgetRow.created_at).toLocaleString()}</div>
          </Col>
          <Col md={6}>
            <div className="mb-2">
              <strong>Status:</strong> {budgetRow.status ? (
                <Badge bg={getLibStatusVariant(budgetRow.status)}>
                  {budgetRow.status}
                </Badge>
              ) : (
                <Badge bg="warning">Pending</Badge>
              )}
              {getNextLibApprovalStep() && (
                <div className="text-muted small mt-1 d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                  <i className="bi bi-eye me-1" aria-hidden="true"></i>
                  <span>{getNextLibApprovalStep()}</span>
                </div>
              )}
            </div>
            {/* Approval Checklist */}
            <div className="mt-2">
              {getLibApprovalChecklist().map(step => (
                <div key={step.key} className="d-flex align-items-center text-muted small">
                  <i className={`bi ${step.done ? 'bi-check-square-fill text-success' : step.rejected ? 'bi-x-square-fill text-danger' : 'bi-square'} me-1`} aria-hidden="true"></i>
                  <span>{step.label}</span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
        
        {/* LIB Approval Workflow - Action Buttons */}
        {budgetRow.status && canCurrentUserDecideLib() && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="d-flex justify-content-end">
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => {
                    setDecision('Approved');
                    setApprovalTarget('budget');
                    setShowDecision(true);
                  }}
                  title="Approve LIB"
                  disabled={hasUserAlreadyApprovedLib()}
                >
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setDecision('Rejected');
                    setApprovalTarget('budget');
                    setShowDecision(true);
                  }}
                  title="Reject LIB"
                  disabled={hasUserAlreadyApprovedLib()}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </div>
            </div>
            
            {budgetRow.remarks && (
              <div className="mt-2">
                <strong className="text-muted">Remarks:</strong>
                <div className="text-muted small" style={{ whiteSpace: 'pre-wrap' }}>{budgetRow.remarks}</div>
              </div>
            )}
          </div>
        )}
        
        {budgetRow.remarks && !canCurrentUserDecideLib() && (
          <div className="mb-3 p-3 bg-light rounded">
            <strong className="text-muted">Remarks:</strong>
            <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>{budgetRow.remarks}</div>
          </div>
        )}
        <div className="table-responsive">
          <Table bordered hover>
            <thead>
              <tr className="table-secondary">
                <th style={{ width: '70%' }}>ITEM</th>
                <th style={{ width: '30%' }}>AMOUNT (Php)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="table-light">
                <td colSpan={2} className="fw-bold">I. Maintenance and Other Operating Expenses (MOOE)</td>
              </tr>
              {mooeItems.map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td>{currency(num(items[key]))}</td>
                </tr>
              ))}

              <tr className="table-light">
                <td colSpan={2} className="fw-bold">II. Equipment Outlay</td>
              </tr>
              {equipmentItems.map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td>{currency(num(items[key]))}</td>
                </tr>
              ))}
              <tr className="table-primary">
                <td className="text-end fw-bold">TOTAL PROGRAM/PROJECT COST</td>
                <td className="fw-bold">{currency(num(grandTotal))}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const statusVariant = (status) => {
    switch (status) {
      case 'Approved':
      case 'Approved by Extension Head':
      case 'Approved by VC':
        return 'success';
      case 'Rejected':
      case 'Rejected by Extension Head':
      case 'Rejected by VC':
        return 'danger';
      case 'On-Going':
      case 'Pending':
      case 'Reviewed by GAD':
        return 'warning';
      case 'Completed':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Compute the next approval step text based on current status, SDGs, and fund source
  const getNextApprovalStep = (p) => {
    const sdgs = asList(p.sdg_goals);
    const fundSource = asList(p.fund_source);
    const needsGAD = sdgs.includes('Gender Equality');
    // More robust STF/MDS detection for frontend
    let hasSTFMDS = false;
    
    // Check array format
    if (Array.isArray(fundSource)) {
      hasSTFMDS = fundSource.includes('STF') || fundSource.includes('MDS');
    }
    
    // Check string format (fallback)
    if (!hasSTFMDS && typeof p.fund_source === 'string') {
      hasSTFMDS = p.fund_source.includes('STF') || p.fund_source.includes('MDS');
    }
    
    // Final fallback - direct string check
    if (!hasSTFMDS && p.fund_source) {
      const fundSourceStr = String(p.fund_source);
      hasSTFMDS = fundSourceStr.includes('STF') || fundSourceStr.includes('MDS');
    }
    const s = p.status;
    
    console.log('🔍 FRONTEND DEBUG - Project approval step:', {
      projectId: p.project_id,
      status: s,
      fundSource: p.fund_source,
      parsedFundSource: fundSource,
      hasSTFMDS: hasSTFMDS,
      needsGAD: needsGAD
    });
    
    // Rejected and Completed/On-Going have no next step in the approval chain
    if (s === 'Rejected' || s === 'Rejected by Extension Head' || s === 'Rejected by VC-AF' || s === 'Rejected by Chancellor' || s === 'Completed' || s === 'On-Going') {
      return null;
    }
    
    // Determine workflow based on project characteristics
    const needsVCAF = hasSTFMDS;
    
    // GAD review (conditional on Gender Equality)
    if (needsGAD && s === 'Pending') return 'To be reviewed by GAD';
    if (needsGAD && s === 'Reviewed by GAD') return 'To be reviewed by Extension Head';
    
    // Extension Head approval
    if (s === 'Pending' && !needsGAD) return 'To be reviewed by Extension Head';
    if (s === 'Approved by Extension Head') return 'To be reviewed by VC for RDES';
    
    // VC-RDES approval
    if (s === 'Approved by VC-RDES') {
      return needsVCAF ? 'To be reviewed by Vice Chancellor for Administration and Finance' : 'To be reviewed by Chancellor';
    }
    
    // VC-AF approval (conditional on STF/MDS)
    if (s === 'Approved by VC-AF') return 'To be reviewed by Chancellor';
    
    // Final approval
    if (s === 'Approved by Chancellor') return null;
    return null;
  };

  // Build approval checklist with completed flags based on project status and characteristics
  const getApprovalChecklist = (p) => {
    const sdgs = asList(p.sdg_goals);
    const needsGAD = sdgs.includes('Gender Equality');
    const fundSource = asList(p.fund_source);
    let hasSTFMDS = false;
    if (Array.isArray(fundSource)) {
      hasSTFMDS = fundSource.includes('STF') || fundSource.includes('MDS');
    }
    if (!hasSTFMDS && typeof p.fund_source === 'string') {
      try {
        if (p.fund_source.trim().startsWith('[')) {
          const parsed = JSON.parse(p.fund_source);
          if (Array.isArray(parsed)) {
            hasSTFMDS = parsed.includes('STF') || parsed.includes('MDS');
          }
        } else {
          hasSTFMDS = p.fund_source.includes('STF') || p.fund_source.includes('MDS');
        }
      } catch(_) {}
    }
    if (!hasSTFMDS && p.fund_source) {
      const fundSourceStr = String(p.fund_source);
      hasSTFMDS = fundSourceStr.includes('STF') || fundSourceStr.includes('MDS');
    }

    const steps = [];
    // GAD step is conditional
    if (needsGAD) {
      steps.push({ key: 'gad', label: 'Reviewed by GAD', done: p.status !== 'Pending' && p.status !== 'Reviewed by GAD' ? true : p.status === 'Reviewed by GAD' });
    }
    steps.push({ key: 'eh', label: 'Approved by Extension Head', done: ['Approved by Extension Head','Approved by VC-RDES','Rejected by VC-RDES','Approved by VC-AF','Rejected by VC-AF','Approved by Chancellor','Rejected by Chancellor','On-Going','Completed'].includes(p.status) });
    steps.push({ key: 'vcrdes', label: 'Approved by VC-RDES', done: ['Approved by VC-RDES','Rejected by VC-RDES','Approved by VC-AF','Rejected by VC-AF','Approved by Chancellor','Rejected by Chancellor','On-Going','Completed'].includes(p.status) });
    if (hasSTFMDS) {
      steps.push({ key: 'vcaf', label: 'Approved by VC-AF', done: ['Approved by VC-AF','Rejected by VC-AF','Approved by Chancellor','Rejected by Chancellor','On-Going','Completed'].includes(p.status) });
    }
    steps.push({ key: 'ch', label: 'Approved by Chancellor', done: ['Approved by Chancellor','Rejected by Chancellor','On-Going','Completed'].includes(p.status) });
    return steps;
  };


  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <div className="d-flex justify-content-end align-items-center mb-3" style={{ marginBottom: '1rem' }}>
        <div className="d-flex gap-2">
          {/* Approve/Reject buttons - based on role and status in workflow */}
          {canCurrentUserDecide(project) && (
            <>
              <Button 
                size="sm" 
                variant="success" 
                style={{ minWidth: '40px', height: '32px' }} 
                onClick={() => { 
                  setDecision('Approved'); 
                  setApprovalTarget('project');
                  setShowDecision(true);
                }}
                title={user.role === 'GAD' ? 'Mark Reviewed' : 'Approve'}
                disabled={hasUserAlreadyApprovedProject(project)}
              >
                <i className="bi bi-check-lg"></i>
              </Button>
              {user.role !== 'GAD' && (
                <Button 
                  size="sm" 
                  variant="danger" 
                  style={{ minWidth: '40px', height: '32px' }} 
                  onClick={() => { 
                    setDecision('Rejected'); 
                    setApprovalTarget('project');
                    setShowDecision(true);
                  }}
                  title="Reject"
                  disabled={hasUserAlreadyApprovedProject(project)}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              )}
            </>
          )}

          {/* Start/End Project buttons removed per request */}

          {/* Edit button - using new permission system */}
          {user && project && (() => {
            const permissions = getItemPermissions(user, project, 'project');
            return permissions.canEdit;
          })() && (
            <Button 
              onClick={() => setShowEditModal(true)}
              variant="outline-primary" 
              size="sm" 
              style={{ minWidth: '40px', height: '32px' }}
              title="Edit"
            >
              <i className="bi bi-pencil"></i>
            </Button>
          )}

          {/* Lock/Unlock Edit Permission button - for elevated roles */}
          {user && project && canToggleEditLock(user) && (
            <Button 
              variant={project.edit_locked ? "warning" : "outline-secondary"}
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleToggleEditLock}
              disabled={editLockLoading}
              title={project.edit_locked ? "Unlock Edit Permission" : "Lock Edit Permission"}
            >
              <i className={`bi ${project.edit_locked ? 'bi-lock-fill' : 'bi-unlock'}`}></i>
            </Button>
          )}

          {/* Repropose button - visible to proposer on any rejected status */}
          {user && ['Rejected','Rejected by Extension Head','Rejected by VC-RDES','Rejected by VC'].includes(project.status) && project.coordinator_id === user.user_id && (
            <Button 
              size="sm" 
              variant="warning" 
              style={{ minWidth: '40px', height: '32px' }}
              onClick={handleRepropose}
              title="Resubmit this project for approval"
            >
              <i className="bi bi-arrow-clockwise"></i>
            </Button>
          )}



          {/* Generate DOCX button - enabled for all projects */}
          <Button 
            variant="outline-primary" 
            size="sm"
            style={{ minWidth: '40px', height: '32px' }}
            onClick={() => setShowDocxTemplateModal(true)}
            disabled={generatingDocx}
            title="Generate document"
          >
            {generatingDocx ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-file-earmark-word"></i>
            )}
          </Button>

          {/* Delete button - using new permission system */}
          {user && project && (() => {
            const permissions = getItemPermissions(user, project, 'project');
            return permissions.canDelete;
          })() && (
            <Button 
              variant="outline-danger" 
              size="sm"
              style={{ minWidth: '40px', height: '32px' }}
              onClick={() => setShowDeleteModal(true)}
              title="Delete Project"
            >
              <i className="bi bi-trash"></i>
            </Button>
          )}

          <Button 
            as={Link} 
            to="/projects" 
            variant="secondary" 
            size="sm" 
            style={{ minWidth: '40px', height: '32px' }}
            title="Back"
          >
            <i className="bi bi-arrow-left"></i>
          </Button>
        </div>
      </div>
      <Card className="shadow-sm">
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          
          {/* Project Title */}
          <div className="mb-4">
            <h4 
              className="text-dark mb-2" 
              style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                maxWidth: '100%'
              }}
              title={project.title}
            >
              {project.title}
            </h4>
          </div>
          
          <Row className="mb-3">
            <Col md={6}>
              <div><strong>Proposer:</strong> {project.coordinator_fullname || '—'}</div>
              <div className="mt-1"><strong>Submitted:</strong> {new Date(project.date_submitted).toLocaleString()}</div>
            </Col>
            <Col md={6}>
              <strong>Status:</strong> <Badge bg={statusVariant(project.status)}>{
                (project.status === 'Approved by VC-AF') ? 'Approved by Vice Chancellor for Administration and Finance'
                : (project.status === 'Rejected by VC-AF') ? 'Rejected by Vice Chancellor for Administration and Finance'
                : project.status
              }</Badge>
              {(() => { const next = getNextApprovalStep(project); return next ? (
                <div className="text-muted small mt-1 d-flex align-items-center" style={{ fontStyle: 'italic' }}>
                  <i className="bi bi-eye me-1" aria-hidden="true"></i>
                  <span>{next}</span>
                </div>
              ) : null; })()}
              {/* Approval Checklist */}
              <div className="mt-2">
                {getApprovalChecklist(project).map(step => (
                  <div key={step.key} className="d-flex align-items-center text-muted small">
                    <i className={`bi ${step.done ? 'bi-check-square-fill text-success' : 'bi-square' } me-1`} aria-hidden="true"></i>
                    <span>{step.label}</span>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}><strong>Request Type:</strong> {project.request_type || '—'}</Col>
            <Col md={6}><strong>Event Type:</strong> {project.event_type || '—'}</Col>
          </Row>
          <Row className="mt-2">
            <Col md={6}><strong>Location:</strong> {project.location || '—'}</Col>
            <Col md={6}>
              <strong>Duration:</strong> 
              {project.start_date ? (
                <div>
                  <div className="row">
                    <div className="col-6">
                      <strong>Start Date:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString() : '—'}
                    </div>
                    <div className="col-6">
                      <strong>Start Time:</strong> {project.start_time ? formatTimeToAMPM(project.start_time) : '—'}
                    </div>
                  </div>
                  {project.end_date && (
                    <div className="row mt-1">
                      <div className="col-6">
                        <strong>End Date:</strong> {new Date(project.end_date).toLocaleDateString()}
                      </div>
                      <div className="col-6">
                        <strong>End Time:</strong> {project.end_time ? formatTimeToAMPM(project.end_time) : '—'}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                '—'
              )}
            </Col>
          </Row>
          
          {/* Remarks section - show for any rejected status */}
          {project.remarks && (
            <Row className="mt-3">
              <Col md={12}>
                {(['Rejected','Rejected by Extension Head','Rejected by VC-RDES','Rejected by VC'].includes(project.status)) ? (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #dc3545' }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-triangle-fill text-danger me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                      <div>
                        <strong className="text-danger">
                          <i className="bi bi-exclamation-circle-fill me-1"></i>Remarks:
                        </strong>
                        <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{project.remarks}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-info border-0" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #28a745' }}>
                    <div className="d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                      <div>
                        <strong className="text-success">
                          <i className="bi bi-info-circle-fill me-1"></i>Remarks:
                        </strong>
                        <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{project.remarks}</div>
                      </div>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          )}
          
          <hr />
          <Row>
            <Col md={6}><strong>Extension Agenda:</strong><div>{asList(project.extension_agenda).join(', ') || '—'}</div></Col>
            <Col md={6}><strong>SDG Goals:</strong><div>{asList(project.sdg_goals).join(', ') || '—'}</div></Col>
          </Row>
          <hr />
          {/* Project leadership section */}
          <div className="mb-3">
            <strong>Project Leader, Assistant Project Leader and Coordinators:</strong>
            <div className="mt-2">
              <div className="mb-2">
                <div style={{ paddingLeft: '0px' }}>
                  <strong>Project Leader:</strong> {project.project_leader_name ? 
                    (() => {
                      const nameOnly = project.project_leader_name.split(' - ')[0];
                      const department = project.project_leader_department || project.department_college || '';
                      return `${nameOnly}${department ? `, ${department}` : ''}`;
                    })() : '—'}
                </div>
                <div style={{ paddingLeft: '0px', marginTop: '4px' }}>
                  <strong>Assistant Project Leader:</strong>
                  {project.assistant_project_leader_names ? 
                    (() => {
                      const nameTaskList = mergeNamesWithTasks(project.assistant_project_leader_names, project.assistant_project_leader_tasks, project.assistant_project_leader_ids, project.assistant_project_leader_departments);
                      return (
                        <div style={{ paddingLeft: '0px', marginTop: '2px' }}>
                          {nameTaskList.map((item, index) => (
                            <div key={index}>
                              {item.name}
                              {item.department && `, ${item.department}`}
                            </div>
                          ))}
                        </div>
                      );
                    })() : null}
                </div>
                <div style={{ paddingLeft: '0px', marginTop: '6px' }}>
                  <strong>Project Staff:</strong>
                  <div style={{ paddingLeft: '0px', marginTop: '2px' }}>
                    {project.project_staff_names ? 
                      (() => {
                        let names = [];
                        try {
                          names = typeof project.project_staff_names === 'string' && project.project_staff_names.trim().startsWith('[')
                            ? JSON.parse(project.project_staff_names)
                            : project.project_staff_names;
                          if (!Array.isArray(names)) names = [];
                        } catch { names = []; }
                        let ids = [];
                        try {
                          ids = typeof project.project_staff_ids === 'string' && project.project_staff_ids.trim().startsWith('[')
                            ? JSON.parse(project.project_staff_ids)
                            : project.project_staff_ids;
                          if (!Array.isArray(ids)) ids = [];
                        } catch { ids = []; }
                        const departmentsById = (() => {
                          try {
                            return project.project_staff_departments && typeof project.project_staff_departments === 'string'
                              ? JSON.parse(project.project_staff_departments)
                              : (project.project_staff_departments || {});
                          } catch { return {}; }
                        })();
                        return names.map((full, index) => {
                          const nameOnly = typeof full === 'string' ? full.split(' - ')[0] : String(full || '');
                          const uid = ids[index];
                          const dept = (departmentsById && departmentsById[uid]) || project.department_college || '';
                          return (
                            <div key={index}>
                              {nameOnly}
                              {dept && `, ${dept}`}
                            </div>
                          );
                        });
                      })() : null}
                  </div>
                </div>
              </div>
              
              <div className="mb-2">
                <div style={{ marginTop: '15px' }}>
                <strong>Assigned Task</strong>
                </div>
              </div>
              
              <div className="table-responsive">
                <table className="table table-sm" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <tbody>
                    {/* Project Leader row */}
                    <tr>
                      <td style={{ width: '40%', padding: '6px', verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {project.project_leader_name ? project.project_leader_name.split(' - ')[0] : '—'}
                      </td>
                      <td style={{ width: '60%', padding: '6px', verticalAlign: 'top' }}>
                        {project.project_leader_task ? 
                          (() => {
                            const items = String(project.project_leader_task)
                              .split(/\r?\n|[;•]|\u2022|\u25CF|\u25E6/)
                              .map(s => s.trim())
                              .filter(Boolean);
                            return items.length ? (
                              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                {items.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            ) : '';
                          })() : ''}
                      </td>
                    </tr>
                    
                    {/* Assistant Leaders rows */}
                    {project.assistant_project_leader_names ? 
                      (() => {
                        const nameTaskList = mergeNamesWithTasks(project.assistant_project_leader_names, project.assistant_project_leader_tasks, project.assistant_project_leader_ids, project.assistant_project_leader_departments);
                        return nameTaskList.map((item, index) => (
                          <tr key={`apl-${index}`}>
                            <td style={{ width: '40%', padding: '6px', verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {item.name}{item.department && `, ${item.department}`}
                            </td>
                            <td style={{ width: '60%', padding: '6px', verticalAlign: 'top' }}>
                              {item.task ? 
                                (() => {
                                  const items = String(item.task)
                                    .split(/\r?\n|[;•]|\u2022|\u25CF|\u25E6/)
                                    .map(s => s.trim())
                                    .filter(Boolean);
                                  return items.length ? (
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                      {items.map((taskItem, taskIndex) => (
                                        <li key={taskIndex}>{taskItem}</li>
                                      ))}
                                    </ul>
                                  ) : '';
                                })() : ''}
                            </td>
                          </tr>
                        ));
                      })() : null}
                    
                    {/* Coordinators rows */}
                    {project.coordinator_names ? 
                      (() => {
                        const nameTaskList = mergeNamesWithTasks(project.coordinator_names, project.coordinator_tasks, project.coordinator_ids, project.coordinator_departments);
                        return nameTaskList.map((item, index) => (
                          <tr key={`coord-${index}`}>
                            <td style={{ width: '40%', padding: '6px', verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {item.name}{item.department && `, ${item.department}`}
                            </td>
                            <td style={{ width: '60%', padding: '6px', verticalAlign: 'top' }}>
                              {item.task ? 
                                (() => {
                                  const items = String(item.task)
                                    .split(/\r?\n|[;•]|\u2022|\u25CF|\u25E6/)
                                    .map(s => s.trim())
                                    .filter(Boolean);
                                  return items.length ? (
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                      {items.map((taskItem, taskIndex) => (
                                        <li key={taskIndex}>{taskItem}</li>
                                      ))}
                                    </ul>
                                  ) : '';
                                })() : ''}
                            </td>
                          </tr>
                        ));
                      })() : null}
                    
                    {/* Project Staff rows */}
                    {project.project_staff_names ? 
                      (() => {
                        const nameTaskList = mergeNamesWithTasks(project.project_staff_names, project.project_staff_tasks, project.project_staff_ids, project.project_staff_departments);
                        return nameTaskList.map((item, index) => (
                          <tr key={`staff-${index}`}>
                            <td style={{ width: '40%', padding: '6px', verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {item.name}{item.department && `, ${item.department}`}
                            </td>
                            <td style={{ width: '60%', padding: '6px', verticalAlign: 'top' }}>
                              {item.task ? 
                                (() => {
                                  const items = String(item.task)
                                    .split(/\r?\n|[;•]|\u2022|\u25CF|\u25E6/)
                                    .map(s => s.trim())
                                    .filter(Boolean);
                                  return items.length ? (
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                      {items.map((taskItem, taskIndex) => (
                                        <li key={taskIndex}>{taskItem}</li>
                                      ))}
                                    </ul>
                                  ) : '';
                                })() : ''}
                            </td>
                          </tr>
                        ));
                      })() : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <hr />
          {/* Offices and Programs */}
          {[
            ['offices_involved','Office/s / College/s / Organization/s Involved'],
            ['programs_involved','Program/s Involved'],
            ['partner_agencies','Partner Agencies'],
            ['beneficiaries','Beneficiaries']
          ].map(([key, label]) => (
            <div className="mb-3" key={key}>
              <strong>{label}:</strong>
              <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project[key] || '—'}</div>
            </div>
          ))}

          {/* Total Cost with peso sign */}
          <div className="mb-3">
            <strong>Total Cost:</strong>
            <div className="mt-1">
              {project.total_cost ? `₱${formatNumberWithCommas(project.total_cost)}` : '—'}
            </div>
          </div>

          {/* Source of Fund placed after Total Cost (as in proposal) */}
          <div className="mb-3">
            <strong>Source of Fund:</strong>
            <div className="mt-1">
              {(() => {
                if (!project.fund_source) return '—';
                try {
                  const parsed = typeof project.fund_source === 'string' ? JSON.parse(project.fund_source) : project.fund_source;
                  if (Array.isArray(parsed)) {
                    return parsed.join(', ');
                  }
                  return String(parsed);
                } catch {
                  return String(project.fund_source);
                }
              })()}
            </div>
          </div>

          {/* Narrative Sections */}
          {/* Narrative Sections except Financial Plan (render separately as table) */}
          {[ 
            ['rationale','Rationale'],
            ['objectives','Objectives (General and Specific)'],
            ['expected_output','Expected Output'],
            ['strategies_methods','Description, Strategies and Methods'],
            ['functional_relationships','Functional Relationships']
          ].map(([key, label]) => (
            <div className="mb-3" key={key}>
              <strong>{label}:</strong>
              <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project[key] || '—'}</div>
            </div>
          ))}

          {/* Financial Plan Table */}
          <div className="mb-3">
            <strong>Financial Plan:</strong>
            <div className="mt-2">
              {(() => {
                const raw = project.financial_plan_details;
                if (!raw) return <div>—</div>;
                let rows;
                try { rows = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { rows = null; }
                if (!Array.isArray(rows) || rows.length === 0) return <div style={{ whiteSpace: 'pre-wrap' }}>{raw}</div>;
                const currency = (v) => {
                  const n = parseFloat(v);
                  return Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (v || '');
                };
                const grand = rows.reduce((sum, r) => sum + (parseFloat(r.total) || 0), 0);
                return (
                  <div className="table-responsive" style={{ border: '1px solid #dee2e6' }}>
                    <table className="table table-bordered table-sm mb-0" style={{ borderCollapse: 'collapse' }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '50%' }}>Item Description</th>
                          <th style={{ width: '10%', textAlign: 'center' }}>Quantity</th>
                          <th style={{ width: '10%', textAlign: 'center' }}>Unit</th>
                          <th style={{ width: '15%', textAlign: 'right' }}>Price (Php)</th>
                          <th style={{ width: '15%', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, idx) => (
                          <tr key={`fpv-${idx}`}>
                            <td>{r.description || ''}</td>
                            <td className="text-center">{r.quantity || ''}</td>
                            <td className="text-center">{r.unit || ''}</td>
                            <td className="text-end">{currency(r.price)}</td>
                            <td className="text-end">{currency(r.total)}</td>
                          </tr>
                        ))}
                        <tr className="table-light">
                          <td colSpan={4} className="text-end fw-bold">TOTAL</td>
                          <td className="text-end fw-bold">{currency(grand)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Monitoring & Evaluation after narrative sections */}
          <div className="mb-3">
            <strong>Monitoring and Evaluation Plan:</strong>
            <div className="mt-2">
              {renderMonitoringEvaluationTable(project.monitoring_evaluation)}
              {(() => {
                // Try to load free_text_rows from either monitoring_evaluation or monitoring_evaluation_table
                let rows = [];
                const tryParse = (v) => { try { return typeof v === 'string' ? JSON.parse(v) : v; } catch { return null; } };
                const a = tryParse(project.monitoring_evaluation);
                const b = tryParse(project.monitoring_evaluation_table);
                const src = (a && a.free_text_rows) ? a : (b && b.free_text_rows ? b : null);
                if (src && Array.isArray(src.free_text_rows)) rows = src.free_text_rows;
                if (!rows.length) return null;
                return (
                  <div className="mt-3">
                    <h6>Additional Notes</h6>
                    <div>
                      {rows.map((t, i) => (
                        <div key={`me-note-${i}`} className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{t}</div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Sustainability Plan last */}
          <div className="mb-3">
            <strong>Sustainability Plan:</strong>
            <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{project.sustainability_plan || '—'}</div>
          </div>

          {budget && renderBudgetTable(budget)}
        </Card.Body>
      </Card>

      {/* Approve/Reject Modal */}
      <Modal show={showDecision} onHide={() => { setShowDecision(false); setApprovalTarget(null); setRemarks(''); }}>
        <Modal.Header closeButton>
          <Modal.Title>
            {decision === 'Approved' 
              ? (approvalTarget === 'budget' ? 'Approve Line-Item Budget' : 'Approve Project')
              : (approvalTarget === 'budget' ? 'Reject Line-Item Budget' : 'Reject Project')
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {decision === 'Approved' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-question-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">
                  {approvalTarget === 'budget' ? 'Confirm Line-Item Budget Approval' : 'Confirm Project Approval'}
                </h5>
                <p className="text-muted">
                  {approvalTarget === 'budget' 
                    ? 'Are you sure you want to approve this Line-Item Budget?'
                    : 'Are you sure you want to approve this project?'
                  }
                </p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Add any additional comments or notes..."
                />
              </Form.Group>
            </>
          )}
          {decision === 'Rejected' && (
            <>
              <div className="text-center py-3">
                <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3 mb-2">
                  {approvalTarget === 'budget' ? 'Confirm Line-Item Budget Rejection' : 'Confirm Project Rejection'}
                </h5>
                <p className="text-muted">
                  {approvalTarget === 'budget' 
                    ? 'Are you sure you want to reject this Line-Item Budget?'
                    : 'Are you sure you want to reject this project?'
                  }
                </p>
              </div>
              <Form.Group>
                <Form.Label>Remarks (optional)</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={remarks} 
                  onChange={(e) => setRemarks(e.target.value)} 
                  placeholder="Please provide a reason for rejection..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDecision(false); setApprovalTarget(null); setRemarks(''); }}>Cancel</Button>
          <Button 
            variant={decision === 'Approved' ? 'success' : 'danger'} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              submitDecision();
            }}
            type="button"
          >
            {decision === 'Approved' ? 'Approve' : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Complete Project Modal */}
      <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill text-info me-2"></i>
            Complete Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h6 className="text-muted mb-2">Project Details</h6>
            <div className="bg-light p-3 rounded">
              <strong>{project?.title}</strong>
              <div className="text-muted mt-1">
                <small>
                  <i className="bi bi-person-fill me-1"></i>
                  Coordinator: {project?.coordinator_fullname}
                </small>
              </div>
              {project?.end_date && (
                <div className="text-muted mt-1">
                  <small>
                    <i className="bi bi-calendar-event me-1"></i>
                    Scheduled End Date: {new Date(project.end_date).toLocaleDateString()}
                  </small>
                </div>
              )}
            </div>
          </div>

          {isEarlyCompletion ? (
            <Alert variant="warning" className="d-flex align-items-start">
              <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
              <div>
                <strong>Early Completion Detected</strong>
                <div className="mt-1">
                  You are completing this project before its scheduled end date 
                  ({new Date(project?.end_date).toLocaleDateString()}). 
                  Please provide a reason for early completion.
                </div>
              </div>
            </Alert>
          ) : (
            <Alert variant="info" className="d-flex align-items-start">
              <i className="bi bi-info-circle-fill me-2 mt-1"></i>
              <div>
                <strong>Confirm Project Completion</strong>
                <div className="mt-1">
                  Are you sure you want to mark this project as completed? 
                  This action will change the project status and cannot be easily undone.
                </div>
              </div>
            </Alert>
          )}

          {isEarlyCompletion && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                <i className="bi bi-chat-quote me-1"></i>
                Reason for Early Completion *
              </Form.Label>
              <Form.Control 
                as="textarea" 
                rows={4}
                value={completionReason}
                onChange={(e) => setCompletionReason(e.target.value)}
                placeholder="Please explain why this project is being completed before its scheduled end date..."
                className="form-control"
              />
              <Form.Text className="text-muted">
                This information will be logged and may be reviewed by administrators.
              </Form.Text>
            </Form.Group>
          )}

          {error && (
            <Alert variant="danger" className="mt-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setShowCompleteModal(false)}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={submitCompletion}
            disabled={isEarlyCompletion && !completionReason.trim()}
          >
            <i className="bi bi-check-circle-fill me-1"></i>
            {isEarlyCompletion ? 'Complete Early' : 'Complete Project'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Selection Modal */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-file-earmark-text me-2"></i>
            Choose Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <p className="text-muted">Select which template you would like to download or print:</p>
          </div>
          <div className="row g-3">
            <div className="col-12">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <i className="bi bi-file-earmark-text-fill text-primary mb-3" style={{ fontSize: '2.5rem' }}></i>
                  <h5 className="card-title">Project Proposal</h5>
                  <p className="card-text text-muted small">
                    Download or print the complete project proposal form
                  </p>
                  <Button 
                    variant="primary" 
                    onClick={() => {
                      setShowTemplateModal(false);
                      handlePrintProjectProposal();
                    }}
                    className="w-100"
                  >
                    <i className="bi bi-download me-2"></i>
                    Download / Print
                  </Button>
                </div>
              </div>
            </div>
            {(() => {
              const isSTFOrMDS = project.fund_source && (
                project.fund_source.includes('STF') || 
                project.fund_source.includes('MDS') ||
                project.fund_source.includes('Special Trust Fund') ||
                project.fund_source.includes('Miscellaneous and Other Sources')
              );
              
              return isSTFOrMDS ? (
                <div className="col-12">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body text-center p-4">
                      <i className="bi bi-calculator-fill text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                      <h5 className="card-title">Extension Line-Item Budget</h5>
                      <p className="card-text text-muted small">
                        {budget ? 'Download or print the detailed budget breakdown' : 'No line-item budget available for this project'}
                      </p>
                      <Button 
                        variant="success" 
                        onClick={() => {
                          setShowTemplateModal(false);
                          handlePrintBudget();
                        }}
                        disabled={!budget}
                        className="w-100"
                        title={!budget ? 'No line-item budget available' : 'Download or print budget'}
                      >
                        <i className="bi bi-download me-2"></i>
                        Download / Print
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowTemplateModal(false)}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DOCX Template Selection Modal */}
      <Modal show={showDocxTemplateModal} onHide={() => setShowDocxTemplateModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-file-earmark-text me-2"></i>
            Choose Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <p className="text-muted">Select which template you would like to download or print:</p>
          </div>
          <div className="row g-3">
            <div className="col-12">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body text-center p-4">
                  <i className="bi bi-file-earmark-text-fill text-danger mb-3" style={{ fontSize: '2.5rem' }}></i>
                  <h5 className="card-title">Project Proposal</h5>
                  <p className="card-text text-muted small">
                    Download or print the complete project proposal form
                  </p>
                  <Button 
                    variant="primary" 
                    onClick={handleGenerateProjectProposalDocx}
                    disabled={generatingDocx}
                    className="w-100"
                  >
                    {generatingDocx ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="bi bi-download me-2"></i>
                    )}
                    Download / Print
                  </Button>
                </div>
              </div>
            </div>
            {(() => {
              const isSTFOrMDS = project && project.fund_source && (
                (typeof project.fund_source === 'string' && (project.fund_source.includes('STF') || project.fund_source.includes('MDS'))) ||
                (Array.isArray(project.fund_source) && (project.fund_source.includes('STF') || project.fund_source.includes('MDS'))) ||
                (typeof project.fund_source === 'string' && project.fund_source.includes('Special Trust Fund')) ||
                (typeof project.fund_source === 'string' && project.fund_source.includes('Miscellaneous and Other Sources'))
              );
              
              return isSTFOrMDS ? (
                <div className="col-12">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body text-center p-4">
                      <i className="bi bi-calculator-fill text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                      <h5 className="card-title">Extension Line-Item Budget</h5>
                      <p className="card-text text-muted small">
                        {budget ? 'Download or print the detailed budget breakdown' : 'No line-item budget available for this project'}
                      </p>
                      <Button 
                        variant="success" 
                        onClick={handleGenerateLibDocx}
                        disabled={!budget || generatingDocx}
                        className="w-100"
                        title={!budget ? 'No line-item budget available' : 'Download or print budget'}
                      >
                        {generatingDocx ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="bi bi-download me-2"></i>
                        )}
                        Download / Print
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDocxTemplateModal(false)}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Delete Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className="bi bi-trash-fill text-danger" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">Confirm Deletion</h5>
            <p className="text-muted mb-3">
              Are you sure you want to delete this project? This action cannot be undone.
            </p>
            {project && (
              <div className="bg-light p-3 rounded">
                <strong>{project.title}</strong>
                <div className="text-muted mt-1">
                  <small>
                    <i className="bi bi-person-fill me-1"></i>
                    Coordinator: {project.coordinator_fullname}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-calendar-event me-1"></i>
                    Submitted: {new Date(project.date_submitted).toLocaleString()}
                  </small>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteProject}>
            <i className="bi bi-trash-fill me-1"></i>
            Delete Project
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Project Edit Modal - for selecting between Project Proposal and LIB */}
      <ProjectEditModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        project={project}
      />

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <h5 className="mb-0">{successMessage}</h5>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectView;


